import React from 'react';
import { Building2, User, Bed } from 'lucide-react';
import { Room } from '../../../types/accommodation';
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  rooms: Room[];
};

export const RoomStats = ({ rooms }: Props) => {
  const { translations } = useLanguage();

  // Calculate statistics
  const totalRooms = rooms.length;
  const occupiedRooms = rooms.filter((room) => room.o > 0).length;
  const totalCapacity = rooms.reduce((total, room) => total + room.c, 0);
  const totalOccupied = rooms.reduce(
    (total, room) => total + room.o,
    0
  );
  const occupancyRate = Math.round((totalOccupied / totalCapacity) * 100);

  // Calculate gender statistics
  const genderStats = rooms.reduce(
    (stats, room) => {
      room.b.forEach(bed => {
        if (bed.a && bed.a.length > 0) {
          // Use the latest assignment's gender
          const latestAssignment = bed.a[bed.a.length - 1];
          if (latestAssignment.g === 'M') {
            stats.male++;
          } else {
            stats.female++;
          }
        }
      });
      return stats;
    },
    { male: 0, female: 0 }
  );

  const totalAssigned = genderStats.male + genderStats.female;
  const malePercentage = totalAssigned ? Math.round((genderStats.male / totalAssigned) * 100) : 0;
  const femalePercentage = totalAssigned ? Math.round((genderStats.female / totalAssigned) * 100) : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
      {/* Rooms Stats */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Building2 size={20} className="text-primary-500" />
          </div>
          <h3 className="font-medium">
            {translations?.roomStats?.rooms || 'Room Statistics'}
          </h3>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">
              {translations?.roomStats?.occupiedTotal || 'Occupied / Total'}
            </span>
            <span className="text-xl font-bold">
              {occupiedRooms} / {totalRooms}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="h-2 rounded-full bg-primary-500 transition-all"
              style={{ width: `${(occupiedRooms / totalRooms) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Beds Stats */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Bed className="text-primary-500" size={20} />
          </div>
          <h3 className="font-medium">
            {translations?.roomStats?.beds || 'Bed Statistics'}
          </h3>
        </div>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">
              {translations?.roomStats?.occupiedCapacity || 'Occupied / Capacity'}
            </span>
            <span className="text-xl font-bold">
              {totalOccupied} / {totalCapacity}
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                occupancyRate > 90
                  ? 'bg-red-500'
                  : occupancyRate > 70
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${occupancyRate}%` }}
            />
          </div>
        </div>
      </div>

      {/* Gender Distribution */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-50 rounded-lg">
            <User className="text-primary-500" size={20} />
          </div>
          <h3 className="font-medium">
            {translations?.roomStats?.guestDistribution || 'Guest Distribution'}
          </h3>
        </div>
        <div className="space-y-4">
          {/* Male Stats */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-gray-600">
                  {translations?.roomStats?.male || 'Male'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{genderStats.male}</span>
                <span className="text-xs text-gray-500">({malePercentage}%)</span>
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${malePercentage}%` }}
              />
            </div>
          </div>

          {/* Female Stats */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-pink-500" />
                <span className="text-sm text-gray-600">
                  {translations?.roomStats?.female || 'Female'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{genderStats.female}</span>
                <span className="text-xs text-gray-500">({femalePercentage}%)</span>
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-pink-500 transition-all"
                style={{ width: `${femalePercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Occupancy Rate */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-primary-50 rounded-lg">
            <User className="text-primary-500" size={20} />
          </div>
          <h3 className="font-medium">
            {translations?.roomStats?.occupancyRate || 'Occupancy Rate'}
          </h3>
        </div>
        <div className="text-center">
          <div
            className={`text-4xl font-bold mb-2 ${
              occupancyRate > 90
                ? 'text-red-500'
                : occupancyRate > 70
                ? 'text-yellow-500'
                : 'text-green-500'
            }`}
          >
            {occupancyRate}%
          </div>
          <p className="text-sm text-gray-500">
            {occupancyRate > 90
              ? translations?.roomStats?.highOccupancy || 'High Occupancy'
              : occupancyRate > 70
              ? translations?.roomStats?.mediumOccupancy || 'Medium Occupancy'
              : translations?.roomStats?.lowOccupancy || 'Low Occupancy'}
          </p>
        </div>
      </div>
    </div>
  );
};
